import ReactGA from 'react-ga4';
import { getCurrentUTMParams } from './utmTracking';

/**
 * Google Analytics 4 (GA4) Integration
 * 
 * This module provides a centralized interface for tracking analytics events
 * throughout the application using Google Analytics 4.
 * 
 * UTM Parameters:
 * All events automatically include UTM parameters when available.
 * UTM parameters are extracted from the URL or retrieved from localStorage.
 * 
 * SETUP INSTRUCTIONS:
 * ===================
 * 
 * 1. Get a GA4 Measurement ID:
 *    - Go to https://analytics.google.com/
 *    - Create a new GA4 property or use existing one
 *    - Navigate to Admin > Data Streams
 *    - Select your web data stream
 *    - Copy the Measurement ID (format: G-XXXXXXXXXX)
 * 
 * 2. Add to Environment Variables:
 *    - Development: Add to .env.local
 *    - Staging: Add to staging environment config
 *    - Production: Add to production environment config
 *    - Variable name: VITE_GA_MEASUREMENT_ID
 * 
 * 3. Testing Analytics:
 *    - Open Google Analytics
 *    - Go to Configure > DebugView
 *    - Browse your app with analytics enabled
 *    - Events should appear in real-time
 *    - View reports in Reports > Realtime
 * 
 * 4. Viewing Data:
 *    - Real-time: Reports > Realtime
 *    - Events: Reports > Engagement > Events
 *    - User behavior: Reports > Engagement > Pages and screens
 */

// Configuration
const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const IS_PRODUCTION = import.meta.env.VITE_APP_ENV === 'production';
const IS_STAGING = import.meta.env.VITE_APP_ENV === 'staging';
const IS_DEVELOPMENT = import.meta.env.NODE_ENV === 'development';

// Only enable analytics in staging and production
const ANALYTICS_ENABLED = (IS_PRODUCTION || IS_STAGING) && MEASUREMENT_ID;

/**
 * Get UTM parameters and merge with event params
 * @param {Object} eventParams - Original event parameters
 * @returns {Object} Event parameters with UTM data included
 */
const mergeUTMParams = (eventParams = {}) => {
  try {
    const utmParams = getCurrentUTMParams()
    if (utmParams && Object.keys(utmParams).length > 0) {
      // Map UTM parameters to GA4-friendly names
      const utmMapping = {
        utm_source: 'campaign_source',
        utm_medium: 'campaign_medium',
        utm_campaign: 'campaign_name',
        utm_term: 'campaign_term',
        utm_content: 'campaign_content',
        utm_id: 'campaign_id',
        utm_source_platform: 'source_platform',
        utm_creative_format: 'creative_format',
        utm_marketing_tactic: 'marketing_tactic',
      }
      
      const mappedUTM = {}
      Object.keys(utmParams).forEach(key => {
        if (utmMapping[key]) {
          mappedUTM[utmMapping[key]] = utmParams[key]
        }
        // Also include original UTM parameter names
        mappedUTM[key] = utmParams[key]
      })
      
      return { ...eventParams, ...mappedUTM }
    }
    return eventParams
  } catch (error) {
    console.error('[Analytics] Error merging UTM parameters:', error)
    return eventParams
  }
}

/**
 * Initialize Google Analytics 4
 * Should be called once when the app starts
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.debug - Enable debug mode (shows console logs)
 */
export const initializeAnalytics = (options = {}) => {
  if (!ANALYTICS_ENABLED) {
    if (IS_DEVELOPMENT) {
      console.log('[Analytics] Disabled in development environment');
    }
    return;
  }

  try {
    // Check for debug mode in URL
    const urlParams = new URLSearchParams(window.location.search);
    const debugMode = urlParams.get('debug_mode') === 'true';

    // Initialize GA4 with debug mode if specified
    ReactGA.initialize(MEASUREMENT_ID, {
      gaOptions: {
        // Anonymize IP addresses for privacy
        anonymizeIp: true,
        // Cookie settings
        cookieFlags: 'SameSite=None;Secure',
        // Enable debug mode if URL parameter is set
        debug_mode: debugMode,
      },
      gtagOptions: {
        // Send page view automatically (we'll handle this manually)
        send_page_view: false,
      },
    });

    if (debugMode) {
      console.log('[Analytics] Debug mode ENABLED - events will appear in GA4 DebugView');
      console.log('[Analytics] To disable, remove ?debug_mode=true from URL');
    }

    if (options.debug || IS_STAGING) {
      console.log('[Analytics] Initialized with Measurement ID:', MEASUREMENT_ID);
    }
  } catch (error) {
    console.error('[Analytics] Initialization failed:', error);
  }
};

/**
 * Track a page view
 * Call this whenever the route changes
 * 
 * @param {string} path - The page path (e.g., '/dashboard', '/profile')
 * @param {string} title - Optional page title
 * @param {Object} additionalParams - Additional parameters including UTM data
 */
export const trackPageView = (path, title = '', additionalParams = {}) => {
  if (!ANALYTICS_ENABLED) return;

  try {
    const params = mergeUTMParams(additionalParams)
    
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title,
      ...params,
    });

    if (IS_STAGING) {
      console.log('[Analytics] Page view:', path, params);
    }
  } catch (error) {
    console.error('[Analytics] Error tracking page view:', error);
  }
};

/**
 * Track a custom event
 * Use this for tracking user interactions and important actions
 * UTM parameters are automatically included when available
 * 
 * @param {string} eventName - Name of the event (e.g., 'button_click', 'form_submit')
 * @param {Object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (!ANALYTICS_ENABLED) return;

  try {
    const params = mergeUTMParams(eventParams)
    ReactGA.event(eventName, params);

    if (IS_STAGING) {
      console.log('[Analytics] Event:', eventName, params);
    }
  } catch (error) {
    console.error('[Analytics] Error tracking event:', error);
  }
};

/**
 * Track user signup
 * Call this when a user successfully completes registration
 * UTM parameters are automatically included for conversion attribution
 * 
 * @param {string} method - Signup method (e.g., 'email', 'google', 'facebook')
 * @param {Object} additionalParams - Additional parameters including UTM data
 */
export const trackSignup = (method = 'email', additionalParams = {}) => {
  trackEvent('sign_up', {
    method: method,
    ...additionalParams,
  });
};

/**
 * Track user login
 * Call this when a user successfully logs in
 * UTM parameters are automatically included
 * 
 * @param {string} method - Login method (e.g., 'email', 'google', 'facebook')
 * @param {Object} additionalParams - Additional parameters including UTM data
 */
export const trackLogin = (method = 'email', additionalParams = {}) => {
  trackEvent('login', {
    method: method,
    ...additionalParams,
  });
};

/**
 * Track feature usage
 * Use this to track when users interact with key features
 * 
 * @param {string} featureName - Name of the feature being used
 * @param {Object} additionalParams - Additional context about the feature usage
 */
export const trackFeatureUse = (featureName, additionalParams = {}) => {
  trackEvent('feature_use', {
    feature_name: featureName,
    ...additionalParams,
  });
};

/**
 * Track form submissions
 * Call this when a form is successfully submitted
 * 
 * @param {string} formName - Name/identifier of the form
 * @param {Object} additionalParams - Additional form context
 */
export const trackFormSubmit = (formName, additionalParams = {}) => {
  trackEvent('form_submit', {
    form_name: formName,
    ...additionalParams,
  });
};

/**
 * Track CTA (Call-to-Action) button clicks
 * Use this for important button clicks that drive user actions
 * 
 * @param {string} buttonName - Name/identifier of the button
 * @param {Object} additionalParams - Additional context about the button click
 */
export const trackCTAClick = (buttonName, additionalParams = {}) => {
  trackEvent('cta_click', {
    button_name: buttonName,
    ...additionalParams,
  });
};

/**
 * Track search queries
 * Use this to understand what users are looking for
 * 
 * @param {string} searchTerm - The search query
 * @param {Object} additionalParams - Additional search context
 */
export const trackSearch = (searchTerm, additionalParams = {}) => {
  trackEvent('search', {
    search_term: searchTerm,
    ...additionalParams,
  });
};

/**
 * Track camp interactions
 * Custom event for tracking camp-related actions
 * 
 * @param {string} action - The action performed (e.g., 'view', 'bookmark', 'share')
 * @param {string} campId - The ID of the camp
 * @param {Object} additionalParams - Additional context
 */
export const trackCampInteraction = (action, campId, additionalParams = {}) => {
  trackEvent('camp_interaction', {
    action: action,
    camp_id: campId,
    ...additionalParams,
  });
};

/**
 * Track email integration events
 * Custom event for tracking email-related actions
 * 
 * @param {string} action - The action performed (e.g., 'connect', 'scan', 'disconnect')
 * @param {Object} additionalParams - Additional context
 */
export const trackEmailIntegration = (action, additionalParams = {}) => {
  trackEvent('email_integration', {
    action: action,
    ...additionalParams,
  });
};

/**
 * Track social features
 * Custom event for tracking friend/social interactions
 * 
 * @param {string} action - The action performed (e.g., 'send_request', 'accept_request', 'create_group')
 * @param {Object} additionalParams - Additional context
 */
export const trackSocialInteraction = (action, additionalParams = {}) => {
  trackEvent('social_interaction', {
    action: action,
    ...additionalParams,
  });
};

/**
 * Track modal interactions
 * Custom event for tracking modal opens, closes, and interactions
 * 
 * @param {string} eventName - The modal event (e.g., 'camp_detail_modal_opened', 'modal_closed')
 * @param {Object} eventParams - Event parameters
 */
export const trackModalEvent = (eventName, eventParams = {}) => {
  trackEvent(eventName, eventParams);
};

/**
 * Track camp detail modal opens
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.source - Source of the modal open ('discovery', 'my_programs', etc.)
 * @param {string} params.camp_id - Camp ID
 * @param {string} params.camp_name - Camp name
 * @param {boolean} params.has_community_data - Whether camp has community enhancements
 * @param {number} params.community_contribution_count - Number of community contributions
 */
export const trackCampDetailModalOpened = (params) => {
  trackModalEvent('camp_detail_modal_opened', {
    source: params.source,
    camp_id: params.camp_id,
    camp_name: params.camp_name,
    has_community_data: params.has_community_data || false,
    community_contribution_count: params.community_contribution_count || 0,
  });
};

/**
 * Track program detail modal opens
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.source - Source of the modal open
 * @param {string} params.program_id - Program ID
 * @param {string} params.program_status - Program status
 * @param {boolean} params.has_children - Whether program has children assigned
 * @param {boolean} params.is_shared - Whether program is shared with community
 */
export const trackProgramDetailModalOpened = (params) => {
  trackModalEvent('program_detail_modal_opened', {
    source: params.source,
    program_id: params.program_id,
    program_status: params.program_status,
    has_children: params.has_children || false,
    is_shared: params.is_shared || false,
  });
};

/**
 * Track camp save actions
 * 
 * @param {string} eventName - Event name ('camp_saved_for_later', 'camp_save_success', 'camp_save_failed')
 * @param {Object} params - Event parameters
 */
export const trackCampSave = (eventName, params) => {
  trackModalEvent(eventName, {
    camp_id: params.camp_id,
    camp_name: params.camp_name,
    source: params.source || 'discovery_modal',
    has_community_data: params.has_community_data || false,
    error: params.error,
  });
};

/**
 * Track program edit actions
 * 
 * @param {string} eventName - Event name ('program_edit_started', 'program_updated', 'program_update_failed')
 * @param {Object} params - Event parameters
 */
export const trackProgramEdit = (eventName, params) => {
  trackModalEvent(eventName, {
    program_id: params.program_id,
    program_status: params.program_status,
    fields_changed: params.fields_changed,
    duration_ms: params.duration_ms,
    was_shared_after: params.was_shared_after,
    error: params.error,
  });
};

/**
 * Track program deletion
 * 
 * @param {string} eventName - Event name ('program_delete_initiated', 'program_deleted', 'program_delete_cancelled', 'program_delete_failed')
 * @param {Object} params - Event parameters
 */
export const trackProgramDelete = (eventName, params) => {
  trackModalEvent(eventName, {
    program_id: params.program_id,
    program_status: params.program_status,
    error: params.error,
  });
};

/**
 * Track external link clicks
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.url - The URL clicked
 * @param {string} params.source - Source of the click ('discovery_modal', 'program_modal')
 * @param {string} params.camp_id - Camp ID
 */
export const trackExternalLinkClick = (params) => {
  trackModalEvent('external_website_clicked', {
    url: params.url,
    source: params.source,
    camp_id: params.camp_id,
  });
};

/**
 * Track community badge interactions
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.camp_id - Camp ID
 * @param {number} params.contribution_count - Number of contributions
 */
export const trackCommunityBadgeClick = (params) => {
  trackModalEvent('community_badge_clicked', {
    camp_id: params.camp_id,
    contribution_count: params.contribution_count || 0,
  });
};

/**
 * Track modal close
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.mode - Modal mode ('discovery', 'user_program')
 * @param {number} params.time_open_ms - Time modal was open in milliseconds
 * @param {boolean} params.was_edited - Whether user edited the program
 * @param {string} params.camp_id - Camp ID
 */
export const trackModalClosed = (params) => {
  trackModalEvent('modal_closed', {
    mode: params.mode,
    time_open_ms: params.time_open_ms,
    was_edited: params.was_edited || false,
    camp_id: params.camp_id,
  });
};

/**
 * Set user properties
 * Use this to add user-level properties for segmentation
 * 
 * @param {Object} properties - User properties to set
 */
export const setUserProperties = (properties) => {
  if (!ANALYTICS_ENABLED) return;

  try {
    ReactGA.set(properties);

    if (IS_STAGING) {
      console.log('[Analytics] User properties set:', properties);
    }
  } catch (error) {
    console.error('[Analytics] Error setting user properties:', error);
  }
};

/**
 * Set user ID
 * Use this to track users across sessions (only use hashed/anonymized IDs)
 * 
 * @param {string} userId - Anonymous user identifier
 */
export const setUserId = (userId) => {
  if (!ANALYTICS_ENABLED) return;

  try {
    ReactGA.set({ userId: userId });

    if (IS_STAGING) {
      console.log('[Analytics] User ID set:', userId);
    }
  } catch (error) {
    console.error('[Analytics] Error setting user ID:', error);
  }
};

/**
 * Track premium page view
 * Call this when users land on the /premium page
 * 
 * @param {Object} additionalParams - Additional parameters including source attribution
 */
export const trackPremiumPageView = (additionalParams = {}) => {
  trackEvent('premium_page_view', {
    source: 'premium_page',
    ...additionalParams,
  });
};

/**
 * Track premium button click
 * Call this when users click any premium CTA button
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.button_location - Location of button (hero, sticky_header, feature_comparison, plan_card, final_cta, compare_plans)
 * @param {string} params.plan_code - Plan code if applicable (monthly, season, annual)
 * @param {string} params.user_status - User authentication status (authenticated, anonymous)
 * @param {string} params.source - Source of the click
 */
export const trackPremiumButtonClick = (params) => {
  trackEvent('premium_button_click', {
    button_location: params.button_location,
    plan_code: params.plan_code,
    user_status: params.user_status || 'unknown',
    source: params.source || 'premium_page',
    ...params,
  });
};

/**
 * Track purchase initiation
 * Call this when a checkout session is created
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.plan_code - Plan code (monthly, season, annual)
 * @param {string} params.user_status - User authentication status (authenticated, anonymous)
 * @param {string} params.source - Source of purchase initiation (premium_page, billing_section, upsell_card, etc.)
 * @param {string} params.checkout_url - Stripe checkout URL (optional, for debugging)
 */
export const trackPurchaseInitiated = (params) => {
  trackEvent('purchase_initiated', {
    plan_code: params.plan_code,
    user_status: params.user_status || 'unknown',
    source: params.source || 'unknown',
    checkout_url: params.checkout_url,
    ...params,
  });
};

/**
 * Track purchase completion
 * Call this when a subscription purchase is successfully completed
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.plan_code - Plan code (monthly, season, annual)
 * @param {string} params.redirect_source - Source of redirect (stripe_checkout)
 * @param {number} params.time_to_complete - Time from first premium page view to purchase (optional)
 */
export const trackPurchaseCompleted = (params) => {
  trackEvent('purchase_completed', {
    plan_code: params.plan_code,
    redirect_source: params.redirect_source || 'stripe_checkout',
    purchase_status: 'success',
    time_to_complete: params.time_to_complete,
    ...params,
  });
};

/**
 * Track purchase cancellation
 * Call this when checkout is canceled
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.plan_code - Plan code (monthly, season, annual)
 * @param {string} params.redirect_source - Source of redirect (stripe_checkout)
 */
export const trackPurchaseCanceled = (params) => {
  trackEvent('purchase_canceled', {
    plan_code: params.plan_code,
    redirect_source: params.redirect_source || 'stripe_checkout',
    purchase_status: 'canceled',
    ...params,
  });
};

/**
 * Track upsell card click
 * Call this when users interact with premium upsell cards
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.card_type - Type of card (premium_half_page)
 * @param {string} params.card_location - Location of card (blog_sidebar, article_sidebar, blog_list, dashboard, etc.)
 * @param {string} params.source_page - Current page path
 * @param {string} params.user_status - User authentication status (authenticated, anonymous)
 */
export const trackUpsellCardClick = (params) => {
  trackEvent('upsell_card_click', {
    card_type: params.card_type || 'premium_half_page',
    card_location: params.card_location,
    source_page: params.source_page || window.location.pathname,
    user_status: params.user_status || 'unknown',
    ...params,
  });
};

/**
 * Track upsell card view
 * Call this when premium upsell cards are rendered/visible
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.card_type - Type of card (premium_half_page)
 * @param {string} params.card_location - Location of card (blog_sidebar, article_sidebar, blog_list, dashboard, etc.)
 * @param {string} params.source_page - Current page path
 */
export const trackUpsellCardView = (params) => {
  trackEvent('upsell_card_view', {
    card_type: params.card_type || 'premium_half_page',
    card_location: params.card_location,
    source_page: params.source_page || window.location.pathname,
    ...params,
  });
};

/**
 * Track plan selection
 * Call this when users select a subscription plan
 * 
 * @param {Object} params - Event parameters
 * @param {string} params.plan_code - Plan code (monthly, season, annual)
 * @param {string} params.plan_name - Plan name
 * @param {string} params.source - Source of selection (premium_page, billing_section, etc.)
 * @param {string} params.user_status - User authentication status (authenticated, anonymous)
 */
export const trackPlanSelection = (params) => {
  trackEvent('plan_selection', {
    plan_code: params.plan_code,
    plan_name: params.plan_name,
    source: params.source || 'unknown',
    user_status: params.user_status || 'unknown',
    ...params,
  });
};

// Export configuration for debugging
export const analyticsConfig = {
  enabled: ANALYTICS_ENABLED,
  measurementId: MEASUREMENT_ID,
  environment: IS_PRODUCTION ? 'production' : IS_STAGING ? 'staging' : 'development',
};

export default {
  initialize: initializeAnalytics,
  trackPageView,
  trackEvent,
  trackSignup,
  trackLogin,
  trackFeatureUse,
  trackFormSubmit,
  trackCTAClick,
  trackSearch,
  trackCampInteraction,
  trackEmailIntegration,
  trackSocialInteraction,
  trackModalEvent,
  trackCampDetailModalOpened,
  trackProgramDetailModalOpened,
  trackCampSave,
  trackProgramEdit,
  trackProgramDelete,
  trackExternalLinkClick,
  trackCommunityBadgeClick,
  trackModalClosed,
  trackPremiumPageView,
  trackPremiumButtonClick,
  trackPurchaseInitiated,
  trackPurchaseCompleted,
  trackPurchaseCanceled,
  trackUpsellCardClick,
  trackUpsellCardView,
  trackPlanSelection,
  setUserProperties,
  setUserId,
  config: analyticsConfig,
};

